import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControlLabel,
  Checkbox,
  Button,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { filterPermissionsByFeatureFlags } from '../../../featureFlags/permissionFeatureFlags';
import {
  filterOnlyPlatformPermissions,
  filterOutPlatformPermissions,
} from '../../../utils/adminMode';

export type PermissionOption = {
  id?: number | string;
  name?: string;
  description?: string;
};

type SelectionMode = 'id' | 'name';

type Props = {
  open: boolean;
  onClose: (selected: Array<number | string>) => void;
  permissions: PermissionOption[];
  selectedPermissions: Array<number | string>;
  /** Role form uses numeric ids; role catalog stores permission names. */
  selectionMode?: SelectionMode;
  /** Tenant templates: hide platform SaaS permissions (default). */
  excludePlatformPermissions?: boolean;
  /** SaaS Identity roles: only platform permission names. */
  onlyPlatformPermissions?: boolean;
  title?: string;
  readonly?: boolean;
};

function getKey(p: PermissionOption, mode: SelectionMode): string | number | null {
  if (mode === 'name') {
    return p.name ?? null;
  }
  return p.id ?? null;
}

export default function PermissionDialog(props: Props) {
  const {
    open,
    onClose,
    permissions,
    selectedPermissions,
    selectionMode = 'id',
    excludePlatformPermissions = true,
    onlyPlatformPermissions = false,
    title = 'Jogosultságok',
    readonly = false,
  } = props;

  const featureFlags = useSelector((state: any) => state.app.featureFlags);

  const [checkedKeys, setCheckedKeys] = useState<Array<number | string>>([]);
  const [searchQuery, setSearchQuery] = useState('');

  const permissionList = useMemo(
    () => (Array.isArray(permissions) ? permissions : []),
    [permissions]
  );

  const visiblePermissions = useMemo(() => {
    let list = filterPermissionsByFeatureFlags(permissionList, featureFlags);
    if (onlyPlatformPermissions) {
      list = filterOnlyPlatformPermissions(list);
    } else if (excludePlatformPermissions) {
      list = filterOutPlatformPermissions(list);
    }
    return list;
  }, [
    permissionList,
    featureFlags,
    excludePlatformPermissions,
    onlyPlatformPermissions,
  ]);

  const visibleKeySet = useMemo(() => {
    const keys = visiblePermissions
      .map((p) => getKey(p, selectionMode))
      .filter((k): k is number | string => k !== null && k !== undefined);
    return new Set(keys);
  }, [visiblePermissions, selectionMode]);

  /** Permissions belonging to disabled features — keep when saving. */
  const hiddenSelectedKeys = useMemo(() => {
    const selected = Array.isArray(selectedPermissions)
      ? selectedPermissions
      : [];
    return selected.filter((k) => !visibleKeySet.has(k));
  }, [selectedPermissions, visibleKeySet]);

  useEffect(() => {
    if (!open) return;
    setCheckedKeys(
      Array.isArray(selectedPermissions) ? [...selectedPermissions] : []
    );
    setSearchQuery('');
  }, [open, selectedPermissions]);

  const filteredPermissions = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return visiblePermissions;
    return visiblePermissions.filter((permission) =>
      `${permission.description || ''} ${permission.name || ''}`
        .toLowerCase()
        .includes(q)
    );
  }, [visiblePermissions, searchQuery]);

  const filteredKeys = useMemo(
    () =>
      filteredPermissions
        .map((p) => getKey(p, selectionMode))
        .filter((k): k is number | string => k !== null && k !== undefined),
    [filteredPermissions, selectionMode]
  );

  const allFilteredSelected =
    filteredKeys.length > 0 &&
    filteredKeys.every((k) => checkedKeys.includes(k));

  const someFilteredSelected =
    filteredKeys.some((k) => checkedKeys.includes(k)) && !allFilteredSelected;

  const handleToggleOne = (key: number | string, checked: boolean) => {
    if (checked) {
      setCheckedKeys((prev) =>
        prev.includes(key) ? prev : [...prev, key]
      );
    } else {
      setCheckedKeys((prev) => prev.filter((k) => k !== key));
    }
  };

  const handleToggleFiltered = (checked: boolean) => {
    if (checked) {
      setCheckedKeys((prev) => {
        const next = new Set(prev);
        filteredKeys.forEach((k) => next.add(k));
        return Array.from(next);
      });
    } else {
      const remove = new Set(filteredKeys);
      setCheckedKeys((prev) => prev.filter((k) => !remove.has(k)));
    }
  };

  const handleSave = () => {
    const visibleChecked = checkedKeys.filter((k) => visibleKeySet.has(k));
    if (onlyPlatformPermissions) {
      // SaaS roles: drop legacy tenant-function permissions that are no longer editable here.
      onClose(visibleChecked);
      return;
    }
    onClose([...new Set([...visibleChecked, ...hiddenSelectedKeys])]);
  };

  return (
    <Dialog
      open={open}
      onClose={() => onClose(selectedPermissions)}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <TextField
          label="Keresés"
          value={searchQuery}
          autoFocus
          onChange={(e) => setSearchQuery(e.target.value)}
          fullWidth
          margin="normal"
          variant="outlined"
        />

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Kijelölve: {checkedKeys.filter((k) => visibleKeySet.has(k)).length} /{' '}
          {visiblePermissions.length}
          {searchQuery.trim()
            ? ` (szűrt lista: ${filteredKeys.length})`
            : ''}
        </Typography>

        <Grid container spacing={1}>
          <Grid size={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={allFilteredSelected}
                  indeterminate={someFilteredSelected}
                  onChange={(e) => handleToggleFiltered(e.target.checked)}
                  color="primary"
                  disabled={readonly || filteredKeys.length === 0}
                />
              }
              label={
                searchQuery.trim()
                  ? 'Összes kijelölése (szűrt találatok)'
                  : 'Összes kijelölése'
              }
            />
          </Grid>
          {filteredPermissions.map((permission, i) => {
            const key = getKey(permission, selectionMode);
            if (key === null || key === undefined) return null;
            return (
              <Grid key={String(key) || i} size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={checkedKeys.includes(key)}
                      onChange={(e) =>
                        handleToggleOne(key, e.target.checked)
                      }
                      color="primary"
                      disabled={readonly}
                    />
                  }
                  label={permission.description || permission.name}
                />
              </Grid>
            );
          })}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(selectedPermissions)}>Mégse</Button>
        {!readonly && (
          <Button variant="contained" color="primary" onClick={handleSave}>
            Alkalmaz
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
